import { Box, Colors, InfoDot, Text } from '@factorialco/deprecated-design-system'
import { AdditionalHoursDetailsFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { RangeOptionsEnum } from 'modules/shift_management/components/ShiftManagement/TimeInsightsSidepanel/Content/utils'

interface Props {
  color: Colors
  hours: string
  additionalHoursDetails?: AdditionalHoursDetailsFragment[]
  rangeType?: string
}

const StackedBarsTooltip = ({
  color,
  hours,
  additionalHoursDetails = [],
  rangeType = RangeOptionsEnum.year,
}: Props) => {
  const planned = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.planned'
  )

  const additional = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.additional'
  )

  const paidLeave = i18n.t('shift_management.hours_information.approved_leaves')
  const overplanned = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.overplanned'
  )

  const regular = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.regular'
  )

  const remaining = (): string => {
    switch (rangeType) {
      case RangeOptionsEnum.year:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.remaining_max_yearly_hours'
        )
      case RangeOptionsEnum.thisYear:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.remaining_max_yearly_hours'
        )
      default:
        return i18n.t(
          'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.remaining_hours'
        )
    }
  }

  const hoursType = (colorForLabel: Colors): string => {
    switch (colorForLabel) {
      case 'viridian1000':
        return planned
      case 'malibu1000':
        return additional
      case 'champagne1000':
        return paidLeave
      case 'tangerine1000':
        return overplanned
      default:
        return remaining()
    }
  }

  const hoursAmount = (amount: string): string => {
    return i18n.t(
      'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.hours_new',
      {
        hours: Number(amount),
      }
    )
  }

  const equivalentAmount = (rawHours: string, equivalentHours: string) => {
    return i18n.t(
      'shift_management.time_insights.time_balance_content.planned_time.table.stacked_bars.equivalent_hours',
      {
        raw_hours: Number(rawHours),
        equivalent_hours: Number(equivalentHours),
      }
    )
  }

  const hoursDifference = (): string => {
    if (additionalHoursDetails.length) {
      const trackedHours = additionalHoursDetails
        .map((detail) => detail.trackedHours)
        .reduce((acc, currentValue) => acc + Number(currentValue), 0)

      const difference = Number(hours) - trackedHours

      return hoursAmount(String(difference))
    }

    return hoursAmount(hours)
  }

  const plannedHours = color === 'viridian1000'
  const additionalHours = color === 'malibu1000'
  const showDetails = plannedHours || additionalHours

  return (
    <Box minWidth="s240" padding="s6" gap="s8">
      <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <InfoDot size="medium" color={color} />
          <Text.Normal size="200" weight="semibold">
            {hoursType(color)}
          </Text.Normal>
        </Box>
        <Text.Normal size="200" weight="semibold">
          {hoursAmount(hours)}
        </Text.Normal>
      </Box>
      {showDetails && (
        <>
          {plannedHours && (
            <>
              <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
                <Text.Normal size="200" weight="semibold">
                  {regular}
                </Text.Normal>
                <Text.Normal size="200" weight="semibold">
                  {hoursDifference()}
                </Text.Normal>
              </Box>
              {additionalHoursDetails.map((detail, index) => (
                <Box
                  key={index}
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  gap="s8"
                >
                  <Text.Normal size="200" weight="semibold">
                    {detail.name}
                  </Text.Normal>
                  <Text.Normal size="200" weight="semibold">
                    {hoursAmount(detail.trackedHours)}
                  </Text.Normal>
                </Box>
              ))}
            </>
          )}
          {additionalHours && (
            <>
              {additionalHoursDetails.map((detail, index) => (
                <Box
                  key={index}
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  gap="s8"
                >
                  <Box flexDirection="row" gap="s4">
                    <Text.Normal size="200" weight="semibold">
                      {detail.name}
                    </Text.Normal>
                    <Text.Normal size="200" weight="semibold">
                      {equivalentAmount(detail.rawHours, detail.equivalentHours)}
                    </Text.Normal>
                  </Box>
                  <Text.Normal size="200" weight="semibold">
                    {hoursAmount(detail.additionalHours)}
                  </Text.Normal>
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default StackedBarsTooltip
